import React, { useMemo, useState } from 'react';
import { Select, Empty } from 'antd';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment';

import DualAxisAreaChart from '../../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../../shared/globalStyling/styledText';

const { Option } = Select;

// =============================================================================
// BasketItemsChart — trend chart for the V2 Basket Items Performance card.
//
// Migrated off the `shared/charts/DualAxisChart` BizCharts wrapper to our v2
// Recharts primitive. Same structure as V2 Transactions / Display chart:
// Trend eyebrow + inline L/R axis selectors in head, chart body below.
// No mode toggle — V2 basket items data ships byDate only; a Day-of-Week
// aggregate can be added later if the backend starts emitting it.
//
// V2 data shape: rows with { date, transactions, revenue, quantity }.
// Normalized to { xAxis: YYYY-MM-DD, ... } so DualAxisAreaChart's
// labelToKey ("Transactions" → "transactions" etc.) resolves correctly.
// =============================================================================

const AXIS_OPTIONS = ['Transactions', 'Revenue', 'Quantity'];

const BasketItemsChart = ({ byDate }) => {
  const [leftY, setLeftY] = useState('Transactions');
  const [rightY, setRightY] = useState('Revenue');

  const rows = useMemo(() => {
    if (isEmpty(byDate)) return [];
    return byDate
      .map((row) => ({
        ...row,
        xAxis: moment(row.date || row.xAxis).isValid()
          ? moment(row.date || row.xAxis).format('YYYY-MM-DD')
          : row.date || row.xAxis,
      }))
      .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
  }, [byDate]);

  if (isEmpty(rows)) {
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Trend
        </div>

        <div className="campaign-overview__axis-row">
          <div className="campaign-overview__axis-field">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
              Left axis
            </span>
            <Select
              onSelect={(value) => setLeftY(value)}
              className="campaign-overview__axis-select"
              value={leftY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== rightY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#7dafff'} />
                </Option>
              ))}
            </Select>
          </div>

          <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
              Right axis
            </span>
            <Select
              onSelect={(value) => setRightY(value)}
              className="campaign-overview__axis-select"
              value={rightY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== leftY).map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#92d5a7'} />
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="campaign-overview__chart-body">
        <DualAxisAreaChart
          dualChartData={rows}
          leftY={leftY}
          rightY={rightY}
        />
      </div>
    </div>
  );
};

export default BasketItemsChart;
